import { useState, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { User, Mail, Lock, Building2, Shield, Check, X } from "lucide-react";

// Düşen ve Zeminde Biriken Futbol Topları
const SoccerBall = ({ size = 40 }) => (
  <img src="/soccer-ball.png" alt="" width={size} height={size} style={{ width: size, height: size, objectFit: 'contain' }} draggable={false} />
);

const FallingBalls = () => {
  const balls = [
    { left: '2%', delay: '0s', size: 38 }, { left: '5%', delay: '0.4s', size: 28 },
    { left: '8%', delay: '0.9s', size: 48 }, { left: '11%', delay: '1.3s', size: 38 },
    { left: '14%', delay: '1.7s', size: 28 }, { left: '17%', delay: '2.1s', size: 48 },
    { left: '20%', delay: '2.5s', size: 38 }, { left: '23%', delay: '2.9s', size: 28 },
    { left: '26%', delay: '3.3s', size: 48 }, { left: '29%', delay: '3.7s', size: 38 },
    { left: '32%', delay: '4.1s', size: 28 }, { left: '35%', delay: '4.5s', size: 48 },
    { left: '38%', delay: '4.9s', size: 38 }, { left: '41%', delay: '5.3s', size: 28 },
    { left: '44%', delay: '5.7s', size: 48 }, { left: '47%', delay: '6.1s', size: 38 },
    { left: '50%', delay: '6.5s', size: 28 }, { left: '53%', delay: '6.9s', size: 48 },
    { left: '56%', delay: '7.3s', size: 38 }, { left: '59%', delay: '7.7s', size: 28 },
    { left: '62%', delay: '8.1s', size: 48 }, { left: '65%', delay: '8.5s', size: 38 },
    { left: '68%', delay: '8.9s', size: 28 }, { left: '71%', delay: '9.3s', size: 48 },
    { left: '74%', delay: '9.7s', size: 38 }, { left: '77%', delay: '10.1s', size: 28 },
    { left: '80%', delay: '10.5s', size: 48 }, { left: '83%', delay: '10.9s', size: 38 },
    { left: '86%', delay: '11.3s', size: 28 }, { left: '89%', delay: '11.7s', size: 48 },
    { left: '92%', delay: '0.2s', size: 38 }, { left: '95%', delay: '0.6s', size: 28 },
    { left: '98%', delay: '1.0s', size: 48 },
  ];

  return (
    <>
      <div className="absolute inset-0 overflow-hidden pointer-events-none z-0">
        {balls.map((b, i) => (
          <div key={i} className="falling-ball" style={{ left: b.left, animationDelay: b.delay }}>
            <SoccerBall size={b.size} />
          </div>
        ))}
      </div>

      {/* CSS Animasyonları - Uzun Sekme */}
      <style>{`
        .falling-ball {
          position: absolute;
          top: -60px;
          animation: fall-bounce-stay 12s ease-in infinite;
        }
        
        @keyframes fall-bounce-stay {
          0% {
            transform: translateY(0) rotate(0deg);
            opacity: 1;
          }
          /* Hızlı düşüş */
          12% {
            transform: translateY(82vh) rotate(300deg);
          }
          /* 1. Sekme - Yüksek */
          18% {
            transform: translateY(50vh) rotate(420deg);
          }
          /* 1. Yere iniş */
          24% {
            transform: translateY(82vh) rotate(540deg);
          }
          /* 2. Sekme - Orta */
          29% {
            transform: translateY(62vh) rotate(630deg);
          }
          /* 2. Yere iniş */
          34% {
            transform: translateY(83vh) rotate(720deg);
          }
          /* 3. Sekme - Kısa */
          38% {
            transform: translateY(72vh) rotate(790deg);
          }
          /* 3. Yere iniş */
          42% {
            transform: translateY(84vh) rotate(860deg);
          }
          /* 4. Sekme - Çok kısa */
          45% {
            transform: translateY(78vh) rotate(910deg);
          }
          /* Son iniş ve durma */
          48%, 92% {
            transform: translateY(85vh) rotate(950deg);
            opacity: 1;
          }
          /* Kaybol */
          100% {
            transform: translateY(85vh) rotate(950deg);
            opacity: 0;
          }
        }
      `}</style>
    </>
  );
};

// Şifre gücü kontrolü
const checkPasswordStrength = (password) => {
  const checks = {
    minLength: password.length >= 8,
    hasUppercase: /[A-Z]/.test(password),
    hasLowercase: /[a-z]/.test(password),
    hasNumber: /\d/.test(password),
  };
  
  const passedChecks = Object.values(checks).filter(Boolean).length;
  const isValid = Object.values(checks).every(Boolean);
  
  let strength = 'weak';
  let color = 'bg-red-500';
  let text = 'Zayıf';
  
  if (passedChecks === 4) {
    strength = 'strong';
    color = 'bg-green-500';
    text = 'Güçlü';
  } else if (passedChecks >= 3) {
    strength = 'medium';
    color = 'bg-yellow-500';
    text = 'Orta';
  }
  
  return { checks, isValid, strength, color, text, passedChecks };
};

// Şifre Gücü Göstergesi Bileşeni
const PasswordStrengthIndicator = ({ password }) => {
  const { checks, strength, color, text, passedChecks } = useMemo(
    () => checkPasswordStrength(password),
    [password]
  );

  if (!password) return null;

  return (
    <div className="mt-2 space-y-2">
      {/* Güç Çubuğu */}
      <div className="flex items-center gap-2">
        <div className="flex-1 h-1.5 bg-gray-200 rounded-full overflow-hidden">
          <div 
            className={`h-full transition-all duration-300 ${color}`}
            style={{ width: `${(passedChecks / 4) * 100}%` }}
          />
        </div>
        <span className={`text-xs font-medium ${
          strength === 'strong' ? 'text-green-600' : 
          strength === 'medium' ? 'text-yellow-600' : 'text-red-600'
        }`}>
          {text}
        </span>
      </div>
      
      {/* Kontrol Listesi */}
      <div className="grid grid-cols-2 gap-1 text-xs">
        <div className={`flex items-center gap-1 ${checks.minLength ? 'text-green-600' : 'text-gray-400'}`}>
          {checks.minLength ? <Check className="h-3 w-3" /> : <X className="h-3 w-3" />}
          En az 8 karakter
        </div>
        <div className={`flex items-center gap-1 ${checks.hasUppercase ? 'text-green-600' : 'text-gray-400'}`}>
          {checks.hasUppercase ? <Check className="h-3 w-3" /> : <X className="h-3 w-3" />}
          Büyük harf (A-Z)
        </div>
        <div className={`flex items-center gap-1 ${checks.hasLowercase ? 'text-green-600' : 'text-gray-400'}`}>
          {checks.hasLowercase ? <Check className="h-3 w-3" /> : <X className="h-3 w-3" />}
          Küçük harf (a-z)
        </div>
        <div className={`flex items-center gap-1 ${checks.hasNumber ? 'text-green-600' : 'text-gray-400'}`}>
          {checks.hasNumber ? <Check className="h-3 w-3" /> : <X className="h-3 w-3" />}
          Rakam (0-9)
        </div>
      </div>
    </div>
  );
};

const AuthPage = ({ setUser }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [isRegisterMode, setIsRegisterMode] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(false);
  
  useEffect(() => {
    const check = () => setIsDarkMode(document.documentElement.classList.contains('dark'));
    check();
    const observer = new MutationObserver(check);
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
    return () => observer.disconnect();
  }, []);
  
  const [loginData, setLoginData] = useState({ email: "", password: "" });
  const [adminLoginData, setAdminLoginData] = useState({ email: "", password: "" });
  const [showAdminDialog, setShowAdminDialog] = useState(false);
  const [playerData, setPlayerData] = useState({
    email: "",
    password: "",
    name: "",
    role: "player",
    position: "midfielder",
    location: ""
  });
  const [venueOwnerData, setVenueOwnerData] = useState({
    email: "",
    password: "",
    name: "",
    role: "venue_owner",
    location: ""
  });

  // Şifre geçerlilik kontrolü
  const isPlayerPasswordValid = checkPasswordStrength(playerData.password).isValid;
  const isVenuePasswordValid = checkPasswordStrength(venueOwnerData.password).isValid;

  const handleLogin = async (e, isVenueOwner = false) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/login`, loginData);
      localStorage.setItem("token", response.data.access_token);
      setUser(response.data.user);
      toast.success("Giriş başarılı!");
      
      if (response.data.user.role === "venue_owner") {
        navigate("/venue-owner-dashboard");
      } else {
        navigate("/");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Giriş başarısız");
    } finally {
      setLoading(false);
    }
  };

  const handleAdminLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/login`, adminLoginData);
      localStorage.setItem("token", response.data.access_token);
      setUser(response.data.user);
      
      if (response.data.user.isAdmin) {
        setShowAdminDialog(false);
        toast.success("Admin girişi başarılı!");
        setTimeout(() => navigate("/admin"), 100);
      } else {
        localStorage.removeItem("token");
        toast.error("Bu hesap admin yetkisine sahip değil");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Giriş başarısız");
    } finally {
      setLoading(false);
    }
  };

  const handlePlayerRegister = async (e) => {
    e.preventDefault();
    
    // Şifre kontrolü
    if (!isPlayerPasswordValid) {
      toast.error("Lütfen güçlü bir şifre oluşturun");
      return;
    }
    
    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/register`, playerData);
      localStorage.setItem("token", response.data.access_token);
      setUser(response.data.user);
      toast.success("Kayıt başarılı!");
      navigate("/");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Kayıt başarısız");
    } finally {
      setLoading(false);
    }
  };

  const handleVenueOwnerRegister = async (e) => {
    e.preventDefault();
    
    // Şifre kontrolü
    if (!isVenuePasswordValid) {
      toast.error("Lütfen güçlü bir şifre oluşturun");
      return;
    }
    
    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/register`, venueOwnerData);
      localStorage.setItem("token", response.data.access_token);
      setUser(response.data.user);
      toast.success("Kayıt başarılı!");
      navigate("/venue-owner-dashboard");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Kayıt başarısız");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-br from-emerald-50 via-white to-slate-50 dark:bg-dark-bg dark:from-dark-bg dark:via-dark-bg dark:to-dark-bg field-pattern transition-colors duration-300">
      {/* Background decoration */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-10 w-72 h-72 bg-emerald-200 dark:bg-dark-card rounded-full mix-blend-multiply dark:mix-blend-screen filter blur-3xl opacity-20 animate-pulse"></div>
        <div className="absolute bottom-20 right-10 w-72 h-72 bg-green-200 dark:bg-dark-accent rounded-full mix-blend-multiply dark:mix-blend-screen filter blur-3xl opacity-20 animate-pulse" style={{ animationDelay: '2s' }}></div>
      </div>

      {/* Animasyonlu Düşen Futbol Topları */}
      <FallingBalls />

      <Card className="w-full max-w-md glass shadow-2xl relative z-10 dark:bg-dark-card dark:border-dark-accent" data-testid="auth-card">
        <CardHeader className="pb-0" style={{ paddingTop: '28px' }}>
          <div className="flex justify-center" style={{ marginBottom: '20px' }}>
            <img 
              src={isDarkMode ? "/krampon-logo-dark.png" : "/krampon-logo.png"}
              alt="Krampon" 
              className="object-contain"
              style={{ width: '240px', height: 'auto', imageRendering: 'auto', ...(isDarkMode ? {} : { mixBlendMode: 'multiply' }) }}
              data-testid="auth-logo"
            />
          </div>
          <CardDescription className="text-center text-sm dark:text-gray-300" style={{ paddingBottom: '20px' }}>
            Futbolun Yeni Boyutu
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Tabs defaultValue="player" className="w-full">
            <TabsList className="grid w-full grid-cols-2 mb-4">
              <TabsTrigger value="player" className="text-sm" data-testid="player-tab">
                <User className="h-4 w-4 mr-1.5" />
                Oyuncu
              </TabsTrigger>
              <TabsTrigger value="venue" className="text-sm" data-testid="venue-tab">
                <Building2 className="h-4 w-4 mr-1.5" />
                Saha Sahibi
              </TabsTrigger>
            </TabsList>
            
            {/* Player Tab - Login & Register */}
            <TabsContent value="player">
              {!isRegisterMode ? (
                <form onSubmit={handleLogin} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="login-email">E-posta</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
                      <Input
                        id="login-email"
                        data-testid="login-email-input"
                        type="email"
                        placeholder="ornek@krampon.com"
                        className="pl-10"
                        value={loginData.email}
                        onChange={(e) => setLoginData({ ...loginData, email: e.target.value })}
                        required
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="login-password">Şifre</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
                      <Input
                        id="login-password"
                        data-testid="login-password-input"
                        type="password"
                        placeholder="••••••••"
                        className="pl-10"
                        value={loginData.password}
                        onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                        required
                      />
                    </div>
                  </div>
                  <Button 
                    type="submit" 
                    className="w-full text-white border-0"
                    style={{ background: 'rgba(147, 112, 219, 0.65)', backdropFilter: 'blur(4px)' }}
                    onMouseEnter={e => e.currentTarget.style.background = 'rgba(147, 112, 219, 0.85)'}
                    onMouseLeave={e => e.currentTarget.style.background = 'rgba(147, 112, 219, 0.65)'}
                    disabled={loading}
                    data-testid="login-submit-btn"
                  >
                    {loading ? "Giriş yapılıyor..." : "Giriş Yap"}
                  </Button>
                  <div className="text-center space-y-2">
                    <button
                      type="button"
                      onClick={() => setIsRegisterMode(true)}
                      className="text-sm text-gray-500 dark:text-gray-400 hover:underline"
                    >
                      Hesabın yok mu? <span className="font-semibold text-gray-900 dark:text-white">Kayıt Ol</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowAdminDialog(true)}
                      className="text-xs hover:underline flex items-center justify-center gap-1 mx-auto"
                      style={{ color: 'rgba(147, 112, 219, 0.8)' }}
                    >
                      <Shield className="h-3 w-3" />
                      Admin Girişi
                    </button>
                  </div>
                </form>
              ) : (
                <form onSubmit={handlePlayerRegister} className="space-y-3">
                  <div className="space-y-2">
                    <Label htmlFor="player-name">Ad Soyad</Label>
                    <div className="relative">
                      <User className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
                      <Input
                        id="player-name"
                        data-testid="player-name-input"
                        placeholder="Ahmet Yılmaz"
                        className="pl-10"
                        value={playerData.name}
                        onChange={(e) => setPlayerData({ ...playerData, name: e.target.value })}
                        required
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="player-email">E-posta</Label>
                    <Input
                      id="player-email"
                      data-testid="player-email-input"
                      type="email"
                      placeholder="ornek@email.com"
                      value={playerData.email}
                      onChange={(e) => setPlayerData({ ...playerData, email: e.target.value })}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="player-password">Şifre</Label>
                    <Input
                      id="player-password"
                      data-testid="player-password-input"
                      type="password"
                      placeholder="Güçlü şifre oluşturun"
                      value={playerData.password}
                      onChange={(e) => setPlayerData({ ...playerData, password: e.target.value })}
                      required
                    />
                    <PasswordStrengthIndicator password={playerData.password} />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-2">
                      <Label htmlFor="player-position">Pozisyon</Label>
                      <Select 
                        value={playerData.position} 
                        onValueChange={(value) => setPlayerData({ ...playerData, position: value })}
                      >
                        <SelectTrigger data-testid="player-position-select" className="text-sm">
                          <SelectValue placeholder="Seçin" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="goalkeeper">Kaleci</SelectItem>
                          <SelectItem value="defender">Defans</SelectItem>
                          <SelectItem value="midfielder">Orta Saha</SelectItem>
                          <SelectItem value="forward">Forvet</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="player-location">Konum</Label>
                      <Input
                        id="player-location"
                        placeholder="İstanbul"
                        className="text-sm"
                        value={playerData.location}
                        onChange={(e) => setPlayerData({ ...playerData, location: e.target.value })}
                      />
                    </div>
                  </div>
                  <Button 
                    type="submit" 
                    className="w-full text-white border-0 disabled:opacity-50 disabled:cursor-not-allowed"
                    style={{ background: 'rgba(147, 112, 219, 0.65)', backdropFilter: 'blur(4px)' }}
                    onMouseEnter={e => e.currentTarget.style.background = 'rgba(147, 112, 219, 0.85)'}
                    onMouseLeave={e => e.currentTarget.style.background = 'rgba(147, 112, 219, 0.65)'}
                    disabled={loading || !isPlayerPasswordValid}
                    data-testid="player-submit-btn"
                  >
                    {loading ? "Kayıt yapılıyor..." : !isPlayerPasswordValid ? "Güçlü şifre gerekli" : "Kayıt Ol"}
                  </Button>
                  <div className="text-center">
                    <button
                      type="button"
                      onClick={() => setIsRegisterMode(false)}
                      className="text-sm text-gray-500 dark:text-gray-400 hover:underline"
                    >
                      Hesabın var mı? <span className="font-semibold text-gray-900 dark:text-white">Giriş Yap</span>
                    </button>
                  </div>
                </form>
              )}
            </TabsContent>

            {/* Venue Owner Tab - Login & Register */}
            <TabsContent value="venue">
              {!isRegisterMode ? (
                <form onSubmit={(e) => handleLogin(e, true)} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="venue-login-email">E-posta</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
                      <Input
                        id="venue-login-email"
                        type="email"
                        placeholder="saha@ornek.com"
                        className="pl-10"
                        value={loginData.email}
                        onChange={(e) => setLoginData({ ...loginData, email: e.target.value })}
                        required
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="venue-login-password">Şifre</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
                      <Input
                        id="venue-login-password"
                        type="password"
                        placeholder="••••••••"
                        className="pl-10"
                        value={loginData.password}
                        onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                        required
                      />
                    </div>
                  </div>
                  <Button 
                    type="submit" 
                    className="w-full text-white border-0"
                    style={{ background: 'rgba(147, 112, 219, 0.65)', backdropFilter: 'blur(4px)' }}
                    onMouseEnter={e => e.currentTarget.style.background = 'rgba(147, 112, 219, 0.85)'}
                    onMouseLeave={e => e.currentTarget.style.background = 'rgba(147, 112, 219, 0.65)'}
                    disabled={loading}
                  >
                    {loading ? "Giriş yapılıyor..." : "Giriş Yap"}
                  </Button>
                  <div className="text-center space-y-2">
                    <button
                      type="button"
                      onClick={() => setIsRegisterMode(true)}
                      className="text-sm text-gray-500 dark:text-gray-400 hover:underline"
                    >
                      Hesabın yok mu? <span className="font-semibold text-gray-900 dark:text-white">Kayıt Ol</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowAdminDialog(true)}
                      className="text-xs hover:underline flex items-center justify-center gap-1 mx-auto"
                      style={{ color: 'rgba(147, 112, 219, 0.8)' }}
                    >
                      <Shield className="h-3 w-3" />
                      Admin Girişi
                    </button>
                  </div>
                </form>
              ) : (
                <form onSubmit={handleVenueOwnerRegister} className="space-y-3">
                  <div className="p-3 rounded-lg" style={{ background: 'rgba(147, 112, 219, 0.1)', border: '1px solid rgba(147, 112, 219, 0.25)' }}>
                    <p className="text-xs" style={{ color: 'rgba(147, 112, 219, 0.9)' }}>
                      <Building2 className="h-3 w-3 inline mr-1" />
                      Halı sahanızı yönetin ve rezervasyonları takip edin
                    </p>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="venue-name">İşletme Adı</Label>
                    <div className="relative">
                      <Building2 className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
                      <Input
                        id="venue-name"
                        data-testid="venue-name-input"
                        placeholder="Yeşil Arena Spor"
                        className="pl-10"
                        value={venueOwnerData.name}
                        onChange={(e) => setVenueOwnerData({ ...venueOwnerData, name: e.target.value })}
                        required
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="venue-email">E-posta</Label>
                    <Input
                      id="venue-email"
                      data-testid="venue-email-input"
                      type="email"
                      placeholder="ornek@email.com"
                      value={venueOwnerData.email}
                      onChange={(e) => setVenueOwnerData({ ...venueOwnerData, email: e.target.value })}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="venue-password">Şifre</Label>
                    <Input
                      id="venue-password"
                      data-testid="venue-password-input"
                      type="password"
                      placeholder="Güçlü şifre oluşturun"
                      value={venueOwnerData.password}
                      onChange={(e) => setVenueOwnerData({ ...venueOwnerData, password: e.target.value })}
                      required
                    />
                    <PasswordStrengthIndicator password={venueOwnerData.password} />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="venue-location">Şehir / Bölge</Label>
                    <Input
                      id="venue-location"
                      placeholder="İstanbul, Kadıköy"
                      value={venueOwnerData.location}
                      onChange={(e) => setVenueOwnerData({ ...venueOwnerData, location: e.target.value })}
                    />
                  </div>
                  <Button 
                    type="submit" 
                    className="w-full text-white border-0 disabled:opacity-50 disabled:cursor-not-allowed"
                    style={{ background: 'rgba(147, 112, 219, 0.65)', backdropFilter: 'blur(4px)' }}
                    onMouseEnter={e => !e.currentTarget.disabled && (e.currentTarget.style.background = 'rgba(147, 112, 219, 0.85)')}
                    onMouseLeave={e => !e.currentTarget.disabled && (e.currentTarget.style.background = 'rgba(147, 112, 219, 0.65)')}
                    disabled={loading || !isVenuePasswordValid}
                    data-testid="venue-submit-btn"
                  >
                    {loading ? "Kayıt yapılıyor..." : !isVenuePasswordValid ? "Güçlü şifre gerekli" : "Kayıt Ol"}
                  </Button>
                  <div className="text-center">
                    <button
                      type="button"
                      onClick={() => setIsRegisterMode(false)}
                      className="text-sm text-gray-500 dark:text-gray-400 hover:underline"
                    >
                      Hesabın var mı? <span className="font-semibold text-gray-900 dark:text-white">Giriş Yap</span>
                    </button>
                  </div>
                </form>
              )}
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      {/* Admin Login Dialog */}
      <Dialog open={showAdminDialog} onOpenChange={setShowAdminDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-600">
              <Shield className="h-5 w-5" />
              Admin Girişi
            </DialogTitle>
          </DialogHeader>
          
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4">
            <p className="text-xs text-amber-700">
              Bu alan sadece platform yöneticileri içindir
            </p>
          </div>

          <form onSubmit={handleAdminLogin} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="admin-dialog-email">Admin E-posta</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
                <Input
                  id="admin-dialog-email"
                  type="email"
                  placeholder="admin@krampon.com"
                  className="pl-10"
                  value={adminLoginData.email}
                  onChange={(e) => setAdminLoginData({ ...adminLoginData, email: e.target.value })}
                  required
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="admin-dialog-password">Şifre</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
                <Input
                  id="admin-dialog-password"
                  type="password"
                  placeholder="••••••••"
                  className="pl-10"
                  value={adminLoginData.password}
                  onChange={(e) => setAdminLoginData({ ...adminLoginData, password: e.target.value })}
                  required
                />
              </div>
            </div>
            
            <Button 
              type="submit" 
              className="w-full bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700"
              disabled={loading}
            >
              {loading ? "Giriş yapılıyor..." : "Admin Girişi"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AuthPage;
